/**
 * androidBridge.js
 *
 * Utility to communicate with the Android WebView host app.
 * The Android app injects a `window.Android` object that exposes
 * a `saveUserPreferences(jsonString)` method. Calling it tells the
 * native notification-matching engine which user is now active and
 * what their preferences are.
 *
 * Safe to call in a browser — the guard `window.Android?.saveUserPreferences`
 * is a no-op when the interface is not present.
 */

/**
 * Notify the Android host that a user has logged in.
 * Sends uid, email, userType and any relevant preference fields.
 *
 * @param {Object} userData  - Firestore user document data
 * @param {string} uid       - Firebase Auth UID
 */
export const notifyAndroidLogin = (userData, uid) => {
  try {
    if (!window.Android?.saveUserPreferences) return;

    const payload = {
      uid: uid || '',
      email: userData?.email || '',
      userType: userData?.userType || '',
      firstName: userData?.firstName || '',
      lastName: userData?.lastName || '',
      phone: userData?.phone || '',
      studentId: userData?.studentId || null,
      employeeId: userData?.employeeId || null,
      franchiseId: userData?.franchiseId || null,
      refCode: userData?.refCode || null,
    };

    window.Android.saveUserPreferences(JSON.stringify(payload));
    console.log('[AndroidBridge] saveUserPreferences called', payload);
  } catch (err) {
    // Never crash the web app because of a bridge error
    console.warn('[AndroidBridge] saveUserPreferences failed:', err);
  }
};

/**
 * Notify the Android host when a jobseeker's profile preferences change.
 * Call this after the seeker profile is saved successfully.
 *
 * @param {string} uid         - Firebase Auth UID
 * @param {Object} preferences - preferences sub-object from seekerProfile
 * @param {Object} personalInfo - personalInfo sub-object from seekerProfile
 */
export const notifyAndroidProfileUpdate = (uid, preferences, personalInfo) => {
  try {
    if (!window.Android?.saveUserPreferences) return;

    const payload = {
      uid: uid || '',
      // Location
      state: personalInfo?.state || '',
      district: personalInfo?.district || '',
      city: personalInfo?.city || '',
      // Job preferences
      jobTypes: preferences?.jobTypes || [],
      workModes: preferences?.workModes || [],
      targetRoles: preferences?.targetRoles || [],
      expectedSalaryMin: preferences?.expectedSalary?.min || '',
      expectedSalaryMax: preferences?.expectedSalary?.max || '',
      noticePeriod: preferences?.noticePeriod || '',
      willingToRelocate: preferences?.willingToRelocate || false,
    };

    window.Android.saveUserPreferences(JSON.stringify(payload));
    console.log('[AndroidBridge] saveUserPreferences (profile update) called', payload);
  } catch (err) {
    console.warn('[AndroidBridge] saveUserPreferences (profile update) failed:', err);
  }
};

/**
 * Notify the Android host after a job application is submitted.
 * Includes jobType so the native app can show or schedule related-job
 * notifications for the same category.
 *
 * @param {Object} application - Application/job context
 */
export const notifyAndroidApplicationSubmitted = (application) => {
  try {
    if (typeof window === 'undefined') return;

    const payload = {
      event: 'job_application_submitted',
      applicationId: application?.applicationId || '',
      jobId: application?.jobId || '',
      jobTitle: application?.jobTitle || '',
      company: application?.company || '',
      jobType: application?.jobType || '',
      applicantId: application?.applicantId || ''
    };

    if (window.Android?.onJobApplicationSubmitted) {
      window.Android.onJobApplicationSubmitted(JSON.stringify(payload));
      console.log('[AndroidBridge] onJobApplicationSubmitted called', payload);
      return;
    }

    if (window.ReactNativeWebView?.postMessage) {
      window.ReactNativeWebView.postMessage(JSON.stringify(payload));
      console.log('[AndroidBridge] ReactNativeWebView postMessage called', payload);
    }
  } catch (err) {
    console.warn('[AndroidBridge] application submitted notification failed:', err);
  }
};

/**
 * Notify the Android host when a payment is successful.
 * Helps to show a native Toast or update UI when standard web alert() fails in WebView.
 *
 * @param {string} packageName - The name of the purchased package
 */
export const notifyAndroidPaymentSuccess = (packageName) => {
  try {
    const message = `Successfully purchased ${packageName}!`;
    
    if (window.Android?.showToast) {
      window.Android.showToast(message);
      console.log('[AndroidBridge] showToast called', message);
    } else if (window.Android?.onPaymentSuccess) {
      window.Android.onPaymentSuccess(message);
      console.log('[AndroidBridge] onPaymentSuccess called', message);
    } else {
      // Fallback for browsers
      alert(message);
    }
  } catch (err) {
    console.warn('[AndroidBridge] notifyAndroidPaymentSuccess failed:', err);
  }
};
